/**
 * ICE 诊断：只在控制台输出，不在界面上展示。
 *
 * 目的是区分「对方 candidate 根本没送达（信令问题）」和「送达了但连不通（NAT/TURN 问题）」，
 * 这两类故障的处理方式完全不同，不能只报一句「连接失败」。
 */

/** 诊断日志前缀，便于在控制台里过滤。 */
export function logIce(line: string): void {
  // eslint-disable-next-line no-console -- 诊断日志按契约设计直接输出到控制台
  console.log('[fogchess]', line)
}

interface CandidateCounts {
  host: number
  srflx: number
  relay: number
  prflx: number
}

const ATTACH_RETRY_INTERVAL_MS = 200
/** PeerJS 在 connect() 之后很快会创建 RTCPeerConnection，重试有上限以免留下永不停止的定时器。 */
const MAX_ATTACH_RETRIES = 50

function emptyCounts(): CandidateCounts {
  return { host: 0, srflx: 0, relay: 0, prflx: 0 }
}

function countCandidate(counts: CandidateCounts, type: string | undefined): void {
  if (type === 'host' || type === 'srflx' || type === 'relay' || type === 'prflx') {
    counts[type] += 1
  }
}

interface StatsRecord {
  type?: string
  candidateType?: string
  state?: string
  nominated?: boolean
}

function readStatsRecord(value: unknown): StatsRecord {
  if (typeof value !== 'object' || value === null) return {}
  const record = value as Record<string, unknown>
  const result: StatsRecord = {}
  if (typeof record.type === 'string') result.type = record.type
  if (typeof record.candidateType === 'string') result.candidateType = record.candidateType
  if (typeof record.state === 'string') result.state = record.state
  if (typeof record.nominated === 'boolean') result.nominated = record.nominated
  return result
}

/**
 * 输出一份连接快照：各状态 + 双方 candidate 数量 + candidate pair 尝试结果。
 */
export async function logDiagnostics(
  pc: RTCPeerConnection | null | undefined,
  log: (line: string) => void = logIce,
): Promise<void> {
  if (!pc) {
    log('诊断：RTCPeerConnection 尚未创建')
    return
  }
  log(
    `诊断：iceConnectionState=${pc.iceConnectionState} iceGatheringState=${pc.iceGatheringState} signalingState=${pc.signalingState}`,
  )

  if (typeof pc.getStats !== 'function') {
    log('诊断：当前 RTCPeerConnection 不支持 getStats，跳过统计')
    return
  }

  try {
    const stats = await pc.getStats()
    const localTypes: Record<string, number> = {}
    const remoteTypes: Record<string, number> = {}
    const pairs: string[] = []

    stats.forEach((report: unknown) => {
      const record = readStatsRecord(report)
      if (record.type === 'local-candidate' && record.candidateType) {
        localTypes[record.candidateType] = (localTypes[record.candidateType] ?? 0) + 1
      }
      if (record.type === 'remote-candidate' && record.candidateType) {
        remoteTypes[record.candidateType] = (remoteTypes[record.candidateType] ?? 0) + 1
      }
      if (record.type === 'candidate-pair') {
        pairs.push(`${record.state ?? 'unknown'}${record.nominated ? '(nominated)' : ''}`)
      }
    })

    log(`诊断：本地candidate=${JSON.stringify(localTypes)} 对方candidate=${JSON.stringify(remoteTypes)}`)
    log(`诊断：candidate-pair 状态=${pairs.length > 0 ? JSON.stringify(pairs) : '(无)'}`)

    if (Object.keys(remoteTypes).length === 0) {
      log('警告：完全没收到对方任何 candidate，说明问题出在信令交换环节（PeerJS/浏览器兼容性），不是 NAT/TURN 问题。')
    } else if (pairs.length === 0) {
      log('警告：双方都有 candidate，但没有尝试配对，可能是信令时序问题，建议双方都刷新页面重试一次。')
    }
  } catch (error) {
    log(`诊断：getStats 调用失败 ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * 监听底层 RTCPeerConnection，尽早发现「信令握手成功但直连/中继失败」的情况。
 *
 * 注意：必须用 addEventListener，不能用 on- 属性赋值——PeerJS 自己也用 on- 属性
 * 注册了 icecandidate（负责把 candidate 发给对方）等关键处理器，直接赋值会把它覆盖掉。
 */
export function createIceWatcher(
  onFail: (reason: string) => void,
  log: (line: string) => void = logIce,
): {
  attach(dataConn: { peerConnection?: RTCPeerConnection | null }): void
  snapshot(): string
} {
  const counts = emptyCounts()
  let pc: RTCPeerConnection | null = null
  let failedOnce = false
  let retries = 0

  const fail = (reason: string): void => {
    if (failedOnce) return
    failedOnce = true
    onFail(reason)
  }

  const attach = (dataConn: { peerConnection?: RTCPeerConnection | null }): void => {
    const target = dataConn.peerConnection ?? null
    if (!target) {
      if (retries >= MAX_ATTACH_RETRIES) {
        log('诊断：始终没有拿到 RTCPeerConnection，停止 ICE 诊断')
        return
      }
      retries += 1
      setTimeout(() => attach(dataConn), ATTACH_RETRY_INTERVAL_MS)
      return
    }
    if (pc === target) return
    pc = target

    log('RTCPeerConnection 已创建，开始收集 ICE candidate...')

    target.addEventListener('icecandidate', (event) => {
      const candidate = event.candidate
      if (candidate && candidate.type) {
        countCandidate(counts, candidate.type)
        log(`收集到本地 candidate: type=${candidate.type} protocol=${candidate.protocol}`)
      } else if (!candidate) {
        log(`本地 candidate 收集完毕: ${JSON.stringify(counts)}`)
      }
    })

    target.addEventListener('icegatheringstatechange', () => {
      log(`ICE gathering 状态: ${target.iceGatheringState}`)
    })

    target.addEventListener('iceconnectionstatechange', () => {
      const connectionState = target.iceConnectionState
      log(`ICE connection 状态: ${connectionState}`)
      if (connectionState !== 'failed' && connectionState !== 'disconnected') return

      if (counts.relay === 0) {
        log('警告：本地没有收集到任何 relay(TURN) candidate，说明所有 TURN 服务器都没连上（端口未开放/凭证无效/被防火墙拦截）。')
        void logDiagnostics(target, log)
        fail('连接失败：没能建立直连，所有 TURN 服务器也没连上，请更换网络（例如手机热点）后重试。')
        return
      }

      void logDiagnostics(target, log)
      fail('连接失败：双方网络无法直连或中继（NAT/防火墙），请更换网络后重试。')
    })
  }

  return {
    attach,
    snapshot: (): string =>
      `candidate=${JSON.stringify(counts)} iceConnectionState=${pc?.iceConnectionState ?? 'unknown'} iceGatheringState=${pc?.iceGatheringState ?? 'unknown'}`,
  }
}
